import {authService} from '@services/auth.service';
import {isSuperAdmin} from '@utils/roleAccess';

export const TENANT_HEADER = 'X-Tenant-ID';
export const ACTIVE_TENANT_KEY = 'active_tenant_id';
export const TENANT_FORBIDDEN_CODE = 'TENANT_FORBIDDEN';
/** Clínica default del backend (seed `mediworks-default`). */
export const DEFAULT_CLINIC_ID = '00000000-0000-4000-8000-000000000001';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Normaliza tenant/clinic id (UUID de clínica en PostgreSQL). */
export const normalizeTenantId = value => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalized = String(value).trim().toLowerCase();

    return UUID_RE.test(normalized) ? normalized : null;
};

export const getClinicInitial = name => {
    const label = String(name || '').trim();

    if (!label) {
        return '?';
    }

    return label.charAt(0).toUpperCase();
};

export const formatClinicLabel = (id, name) => {
    const label = String(name || '').trim();

    if (label) {
        return label;
    }

    if (!id) {
        return 'Sin clínica';
    }

    const short = String(id).slice(0, 8);
    return `Clínica (${short}…)`;
};

export const parseClinicSignupId = searchParams => {
    if (!searchParams) {
        return null;
    }

    const read = key => searchParams.get(key);

    return normalizeTenantId(
        read('clinic') ||
        read('clinic_id') ||
        read('tenant_id')
    );
};

/** UUID de clínica para signup: query string o clínica default del sistema. */
export const resolveSignupClinicId = searchParams =>
    parseClinicSignupId(searchParams) ?? DEFAULT_CLINIC_ID;

export const isTenantForbiddenError = error => {
    const status = error?.response?.status;
    const code = String(error?.response?.data?.code || '').toUpperCase();

    return status === 403 && code === TENANT_FORBIDDEN_CODE;
};

export const userBelongsToCurrentTenant = (targetUser, options = {}) => {
    const {
        currentTenantId = getActiveTenantId(),
        sessionUser = authService.getUser(),
        allowSuperAdmin = true
    } = options;

    if (allowSuperAdmin && isSuperAdmin(sessionUser)) {
        return true;
    }

    const expectedTenantId = normalizeTenantId(currentTenantId);
    const targetTenantId = getTenantIdFromUser(targetUser);

    if (!expectedTenantId || !targetTenantId) {
        return false;
    }

    return targetTenantId === expectedTenantId;
};

export const filterUsersByCurrentTenant = (users, options = {}) => {
    if (!Array.isArray(users)) {
        return [];
    }

    const sessionUser = options.sessionUser ?? authService.getUser();

    if (isSuperAdmin(sessionUser) && options.filterSuperAdmin !== true) {
        return users;
    }

    return users.filter(user => userBelongsToCurrentTenant(user, {
        ...options,
        sessionUser,
        allowSuperAdmin: false
    }));
};

export const getTenantIdFromUser = user => {
    if (!user) {
        return null;
    }

    return normalizeTenantId(
        user.tenant_id ??
        user.tenantId ??
        user.clinic_id ??
        user.clinicId ??
        user.tenant?.id ??
        user.Tenant?.id ??
        user.clinic?.id ??
        user.Clinic?.id
    );
};

export const getTenantNameFromUser = user => {
    if (!user) {
        return '';
    }

    return String(
        user.tenant_name ??
        user.tenantName ??
        user.clinic_name ??
        user.clinicName ??
        user.tenant?.nombre ??
        user.tenant?.name ??
        user.Tenant?.nombre ??
        user.clinic?.nombre ??
        user.clinic?.name ??
        user.Clinic?.nombre ??
        user.clinica_nombre ??
        user.clinica ??
        ''
    ).trim();
};

export const normalizeAvailableClinics = rawList => {
    if (!Array.isArray(rawList)) {
        return [];
    }

    return rawList
        .map(item => {
            const id = normalizeTenantId(item?.id ?? item?.clinic_id ?? item?.tenant_id ?? item?.tenantId);
            const nombre = String(
                item?.nombre ??
                item?.name ??
                item?.clinic_name ??
                item?.tenant_name ??
                ''
            ).trim();

            return id ? {id, nombre: formatClinicLabel(id, nombre)} : null;
        })
        .filter(Boolean);
};

export const requiresClinicSelection = user => {
    if (!user) {
        return false;
    }

    return !getActiveTenantId();
};

export const getUserTenants = user => {
    if (!user) {
        return [];
    }

    const rawList = user.tenants ??
        user.clinics ??
        user.clinicas ??
        user.available_clinics ??
        user.Tenants ??
        user.Clinics;

    if (!Array.isArray(rawList)) {
        const tenantId = getTenantIdFromUser(user);
        const tenantName = getTenantNameFromUser(user);

        if (tenantId) {
            return [{id: tenantId, nombre: formatClinicLabel(tenantId, tenantName)}];
        }

        return [];
    }

    return rawList
        .map(item => {
            const id = normalizeTenantId(item?.id ?? item?.tenant_id ?? item?.tenantId ?? item?.clinic_id);
            const nombre = String(item?.nombre ?? item?.name ?? item?.tenant_name ?? item?.clinic_name ?? '').trim();

            return id ? {id, nombre: formatClinicLabel(id, nombre)} : null;
        })
        .filter(Boolean);
};

export const getActiveTenantId = () => {
    const override = normalizeTenantId(localStorage.getItem(ACTIVE_TENANT_KEY));

    if (override) {
        return override;
    }

    return getTenantIdFromUser(authService.getUser());
};

export const setActiveTenantId = tenantId => {
    const normalized = normalizeTenantId(tenantId);

    if (!normalized) {
        localStorage.removeItem(ACTIVE_TENANT_KEY);
    } else {
        localStorage.setItem(ACTIVE_TENANT_KEY, normalized);
    }

    window.dispatchEvent(new CustomEvent('tenant-context-changed', {detail: {tenantId: normalized}}));
};

export const clearActiveTenantId = () => {
    localStorage.removeItem(ACTIVE_TENANT_KEY);
};

export const userHasTenantAssignment = user => Boolean(getTenantIdFromUser(user));

export const userCanAccessApp = user => {
    if (!user) {
        return false;
    }

    if (isSuperAdmin(user)) {
        return true;
    }

    return userHasTenantAssignment(user);
};

export const stripTenantFromPayload = payload => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return payload;
    }

    const next = {...payload};
    delete next.tenant_id;
    delete next.tenantId;
    delete next.tenant;
    delete next.clinic_id;
    delete next.clinicId;
    delete next.clinic;
    return next;
};

export const decodeJwtPayload = token => {
    if (!token || typeof token !== 'string') {
        return null;
    }

    try {
        const segment = token.split('.')[1];

        if (!segment) {
            return null;
        }

        const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        return JSON.parse(atob(padded));
    } catch {
        return null;
    }
};

export const getTenantContextFromJwt = token => {
    const jwtPayload = decodeJwtPayload(token);

    if (!jwtPayload) {
        return {
            tenant_id: null,
            tenant_name: '',
            role_global: '',
            role_in_clinic: ''
        };
    }

    return {
        tenant_id: normalizeTenantId(jwtPayload.tenant_id),
        tenant_name: String(jwtPayload.tenant_name ?? '').trim(),
        role_global: String(
            jwtPayload.role_global ?? jwtPayload.role ?? jwtPayload.role_name ?? ''
        ).trim(),
        role_in_clinic: String(jwtPayload.role_in_clinic ?? '').trim()
    };
};

export const mergeUserWithJwtTenant = (user, token, sessionPatch = {}) => {
    if (!user) {
        return null;
    }

    const jwtContext = getTenantContextFromJwt(token);
    const tenantId = getTenantIdFromUser(user) ??
        normalizeTenantId(sessionPatch.tenant_id) ??
        jwtContext.tenant_id;
    const tenantName = getTenantNameFromUser(user) ||
        String(sessionPatch.tenant_name ?? '').trim() ||
        jwtContext.tenant_name;
    const roleGlobal = String(
        user.role_global ??
        user.role_name ??
        sessionPatch.role_global ??
        jwtContext.role_global ??
        ''
    ).trim();
    const roleInClinic = String(
        user.role_in_clinic ??
        sessionPatch.role_in_clinic ??
        jwtContext.role_in_clinic ??
        ''
    ).trim();

    const next = {
        ...user,
        ...(sessionPatch.clinic ? {clinic: sessionPatch.clinic} : {})
    };

    if (tenantId) {
        next.tenant_id = tenantId;
    }

    if (tenantName) {
        next.tenant_name = tenantName;
    }

    if (roleGlobal) {
        next.role_global = roleGlobal;
    }

    if (roleInClinic) {
        next.role_in_clinic = roleInClinic;
    }

    return next;
};
